import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import Button from '../../components/common/Button'
import OnboardingLayout from '../../components/common/OnboardingLayout'
import { esignApi } from '../../services/api'
import { updateOnboardingStep } from '../../store/slices/authSlice'

function openSigningWindow(url: string): boolean {
  const win = window.open(url, '_blank')
  if (!win || win.closed || typeof win.closed === 'undefined') return false
  win.opener = null
  return true
}

const ESignPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [initiating, setInitiating] = useState(true)
  const [signingUrl, setSigningUrl] = useState('')
  const [popupBlocked, setPopupBlocked] = useState(false)
  const [error, setError] = useState('')
  const [attempt, setAttempt] = useState(0)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const next = searchParams.get('next') || '/dashboard/checkout'

  useEffect(() => {
    let cancelled = false

    const completeAndContinue = () => {
      dispatch(updateOnboardingStep('complete'))
      navigate(next)
    }

    const startPolling = () => {
      pollRef.current = setInterval(async () => {
        try {
          const res = await esignApi.getStatus()
          if (res.esign_status === 'completed') {
            if (pollRef.current) clearInterval(pollRef.current)
            completeAndContinue()
          }
        } catch {
          // Keep polling until a definitive status arrives.
        }
      }, 5000)
    }

    const init = async () => {
      try {
        const status = await esignApi.getStatus()
        if (cancelled) return
        if (status.esign_status === 'completed') {
          completeAndContinue()
          return
        }

        const initiated = await esignApi.initiate()
        if (cancelled) return

        if (initiated.esign_status === 'completed') {
          completeAndContinue()
          return
        }

        setSigningUrl(initiated.signing_url)
        setInitiating(false)
        setPopupBlocked(!openSigningWindow(initiated.signing_url))
        startPolling()
      } catch (err: any) {
        if (cancelled) return
        setError(err.response?.data?.detail || 'Failed to initiate eSign')
        setInitiating(false)
      }
    }

    init()

    return () => {
      cancelled = true
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [attempt, dispatch, navigate, next])

  const retry = () => {
    setError('')
    setInitiating(true)
    setAttempt((a) => a + 1)
  }

  return (
    <OnboardingLayout
      currentStep="account"
      showSteps={false}
      title="Sign Client Agreement"
      subtitle="Complete eSign to continue to payment."
      footerText="Your agreement is securely processed and stored for compliance."
    >
      <div className="space-y-6">
        <Link to="/dashboard/subscription" className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-300 transition-colors">
          <ArrowLeftIcon className="w-3.5 h-3.5" />
          Back to plans
        </Link>

        {initiating && (
          <div className="text-center py-10">
            <div className="animate-spin w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-gray-400">Preparing your agreement document...</p>
          </div>
        )}

        {!initiating && error && (
          <div className="space-y-4">
            <div className="bg-red-900/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-md text-sm">{error}</div>
            <Button onClick={retry} className="w-full">Retry</Button>
          </div>
        )}

        {!initiating && !error && (
          <>
            {popupBlocked ? (
              <div className="bg-amber-900/20 border border-amber-500/30 text-amber-300 px-4 py-3 rounded-md text-sm">
                Your browser blocked the signing window. Use the button below to open it.
              </div>
            ) : (
              <div className="bg-blue-900/20 border border-blue-500/30 text-blue-300 px-4 py-3 rounded-md text-sm">
                The agreement opened in a new tab. Sign it there and this page will continue automatically.
              </div>
            )}

            {signingUrl && (
              <Button
                className="w-full"
                size="lg"
                variant={popupBlocked ? 'primary' : 'outline'}
                onClick={() => setPopupBlocked(!openSigningWindow(signingUrl))}
              >
                {popupBlocked ? 'Open Signing Page' : 'Reopen Signing Page'}
              </Button>
            )}

            <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
              <div className="animate-spin w-3 h-3 border-2 border-gray-600 border-t-transparent rounded-full" />
              Waiting for signature confirmation...
            </div>
          </>
        )}
      </div>
    </OnboardingLayout>
  )
}

export default ESignPage
